import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { fetchCurrentUser } from '@/app/slices/authSlice';
import { hasPermission } from '@/types/auth';
import { SectionLoading } from '@/components/ui/SectionLoading';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that enforces:
 * 1. Authentication (valid token required)
 * 2. Authorization (role-based permission check)
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, isInitialized } = useAppSelector((state) => state.auth);

  // Fetch current user on mount if not yet loaded (cookie may still be valid)
  useEffect(() => {
    if (!user && !isLoading && !isInitialized) {
      dispatch(fetchCurrentUser());
    }
  }, [user, isLoading, isInitialized, dispatch]);

  // Show loading spinner while checking auth (loading OR not yet initialized)
  if (isLoading || !isInitialized) {
    return (
      <SectionLoading />
    );
  }

  // Redirect to login if not authenticated (only after initialization is complete)
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // Permission check — deny by default if role is absent or path not permitted (SEC-QY-03)
  const currentPath = location.pathname;
  if (!user?.role || !hasPermission(user.role, currentPath)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          You don't have permission to access this page.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your role: <strong>{user?.role ?? 'none'}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Requested path: <strong>{currentPath}</strong>
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.history.back()}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => (window.location.href = '/dashboard')}
        >
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}
